import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  collectMailContacts,
  deleteMailContact,
  fetchMailContactPhotoBlob,
  fetchMailContacts,
  resolveMailContactPhotoUrl,
  saveMailContact,
  uploadMailContactPhoto,
} from "@/lib/api/contacts";
import {
  deleteMailContactGroup,
  fetchMailContactGroups,
  saveMailContactGroup,
} from "@/lib/api/contactGroups";
import { mailContactDisplayLabel } from "@/lib/mail/contactLabels";
import type {
  MailContact,
  MailContactCollectEntry,
  MailContactSavePayload,
} from "@/lib/types/contact";
import type {
  MailContactGroup,
  MailContactGroupSavePayload,
} from "@/lib/types/contactGroup";

export type MailRecipientSuggestion =
  | { kind: "contact"; contact: MailContact }
  | { kind: "group"; group: MailContactGroup };

type MailContactsContextValue = {
  contacts: MailContact[];
  groups: MailContactGroup[];
  loading: boolean;
  refresh: () => Promise<void>;
  collectEntries: (entries: MailContactCollectEntry[]) => void;
  searchSuggestions: (query: string, limit?: number) => MailContact[];
  searchRecipientSuggestions: (query: string, limit?: number) => MailRecipientSuggestion[];
  getByEmail: (email: string) => MailContact | undefined;
  getPhotoBlob: (contact: MailContact) => string | null;
  saveContact: (payload: MailContactSavePayload) => Promise<MailContact | null>;
  promoteToBook: (contact: MailContact, patch: Omit<MailContactSavePayload, "id" | "promoteToBook">) => Promise<MailContact | null>;
  removeContact: (id: number) => Promise<boolean>;
  uploadPhoto: (contactId: number, file: File) => Promise<MailContact | null>;
  saveGroup: (payload: MailContactGroupSavePayload) => Promise<MailContactGroup | null>;
  removeGroup: (id: number) => Promise<boolean>;
};

const MailContactsContext = createContext<MailContactsContextValue | null>(null);

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function MailContactsProvider({ children }: { children: ReactNode }) {
  const [contacts, setContacts] = useState<MailContact[]>([]);
  const [groups, setGroups] = useState<MailContactGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const photoBlobsRef = useRef(new Map<number, string>());
  const collectTimerRef = useRef<number | null>(null);
  const pendingCollectRef = useRef(new Map<string, MailContactCollectEntry>());

  const refresh = useCallback(async () => {
    setLoading(true);
    const [contactsRes, groupsRes] = await Promise.all([
      fetchMailContacts({ status: "all", limit: 500 }),
      fetchMailContactGroups({ limit: 200 }),
    ]);
    if (contactsRes.ok) {
      setContacts(contactsRes.data.contacts ?? []);
    }
    if (groupsRes.ok) {
      setGroups(groupsRes.data.groups ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const flushCollect = useCallback(() => {
    const entries = [...pendingCollectRef.current.values()];
    pendingCollectRef.current.clear();
    if (entries.length === 0) return;
    void collectMailContacts(entries).then((res) => {
      if (res.ok && (res.data.changed ?? 0) > 0) {
        void refresh();
      }
    });
  }, [refresh]);

  const collectEntries = useCallback(
    (entries: MailContactCollectEntry[]) => {
      if (entries.length === 0) return;
      for (const entry of entries) {
        const email = normalizeEmail(entry.email);
        if (!email) continue;
        pendingCollectRef.current.set(email, {
          email,
          displayName: entry.displayName ?? null,
        });
      }
      if (collectTimerRef.current != null) {
        window.clearTimeout(collectTimerRef.current);
      }
      collectTimerRef.current = window.setTimeout(flushCollect, 600);
    },
    [flushCollect]
  );

  const byEmail = useMemo(() => {
    const map = new Map<string, MailContact>();
    for (const c of contacts) {
      map.set(normalizeEmail(c.email), c);
    }
    return map;
  }, [contacts]);

  const getByEmail = useCallback((email: string) => byEmail.get(normalizeEmail(email)), [byEmail]);

  const searchSuggestions = useCallback(
    (query: string, limit = 8) => {
      const q = query.trim().toLowerCase();
      if (q.length < 1) return [];

      const scored: { contact: MailContact; score: number }[] = [];
      for (const contact of contacts) {
        const label = mailContactDisplayLabel(contact).toLowerCase();
        const email = contact.email.toLowerCase();
        let score = -1;
        if (email.startsWith(q)) score = 100;
        else if (label.startsWith(q)) score = 90;
        else if (email.includes(q)) score = 70;
        else if (label.includes(q)) score = 60;
        else if (contact.company?.toLowerCase().includes(q)) score = 50;
        if (score >= 0) scored.push({ contact, score });
      }

      scored.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        if (a.contact.status !== b.contact.status) {
          return a.contact.status === "book" ? -1 : 1;
        }
        return (b.contact.useCount ?? 0) - (a.contact.useCount ?? 0);
      });

      return scored.slice(0, limit).map((row) => row.contact);
    },
    [contacts]
  );

  const searchRecipientSuggestions = useCallback(
    (query: string, limit = 8): MailRecipientSuggestion[] => {
      const q = query.trim().toLowerCase();
      if (q.length < 1) return [];

      type Scored = { suggestion: MailRecipientSuggestion; score: number };
      const scored: Scored[] = [];

      for (const group of groups) {
        if (group.memberCount < 1) continue;
        const name = group.name.toLowerCase();
        let score = -1;
        if (name.startsWith(q)) score = 110;
        else if (name.includes(q)) score = 85;
        if (score >= 0) {
          scored.push({ suggestion: { kind: "group", group }, score });
        }
      }

      for (const contact of searchSuggestions(query, limit)) {
        const label = mailContactDisplayLabel(contact).toLowerCase();
        const email = contact.email.toLowerCase();
        let score = 70;
        if (email.startsWith(q)) score = 100;
        else if (label.startsWith(q)) score = 90;
        else if (email.includes(q)) score = 70;
        else if (label.includes(q)) score = 60;
        scored.push({ suggestion: { kind: "contact", contact }, score });
      }

      scored.sort((a, b) => b.score - a.score);
      return scored.slice(0, limit).map((row) => row.suggestion);
    },
    [groups, searchSuggestions]
  );

  const getPhotoBlob = useCallback((contact: MailContact) => {
    if (!contact.photoUrl || contact.photoVersion <= 0) return null;
    return photoBlobsRef.current.get(contact.id) ?? null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    const bookWithPhoto = contacts.filter((c) => c.status === "book" && c.photoUrl && c.photoVersion > 0);

    void (async () => {
      for (const contact of bookWithPhoto) {
        if (cancelled || !contact.photoUrl) continue;
        if (photoBlobsRef.current.has(contact.id)) continue;
        const blobUrl = await fetchMailContactPhotoBlob(contact.photoUrl);
        if (cancelled || !blobUrl) continue;
        photoBlobsRef.current.set(contact.id, blobUrl);
        setContacts((prev) => [...prev]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [contacts]);

  useEffect(
    () => () => {
      for (const url of photoBlobsRef.current.values()) {
        URL.revokeObjectURL(url);
      }
      photoBlobsRef.current.clear();
      if (collectTimerRef.current != null) {
        window.clearTimeout(collectTimerRef.current);
      }
    },
    []
  );

  const saveContact = useCallback(async (payload: MailContactSavePayload) => {
    const res = await saveMailContact(payload);
    if (!res.ok) return null;
    const saved = res.data.contact;
    setContacts((prev) => {
      const idx = prev.findIndex((c) => c.id === saved.id);
      if (idx === -1) return [saved, ...prev];
      const next = [...prev];
      next[idx] = saved;
      return next;
    });
    return saved;
  }, []);

  const promoteToBook = useCallback(
    async (contact: MailContact, patch: Omit<MailContactSavePayload, "id" | "promoteToBook">) => {
      return saveContact({
        ...patch,
        id: contact.id,
        email: patch.email || contact.email,
        promoteToBook: true,
      });
    },
    [saveContact]
  );

  const removeContact = useCallback(async (id: number) => {
    const res = await deleteMailContact(id);
    if (!res.ok) return false;
    const blob = photoBlobsRef.current.get(id);
    if (blob) {
      URL.revokeObjectURL(blob);
      photoBlobsRef.current.delete(id);
    }
    setContacts((prev) => prev.filter((c) => c.id !== id));
    return true;
  }, []);

  const uploadPhoto = useCallback(async (contactId: number, file: File) => {
    const res = await uploadMailContactPhoto(contactId, file);
    if (!res.ok) return null;

    const prevBlob = photoBlobsRef.current.get(contactId);
    if (prevBlob) URL.revokeObjectURL(prevBlob);
    photoBlobsRef.current.delete(contactId);

    const absolute = resolveMailContactPhotoUrl(res.data.photoUrl);
    if (absolute) {
      const blobUrl = await fetchMailContactPhotoBlob(res.data.photoUrl);
      if (blobUrl) photoBlobsRef.current.set(contactId, blobUrl);
    }

    let updated: MailContact | null = null;
    setContacts((prev) =>
      prev.map((c) => {
        if (c.id !== contactId) return c;
        updated = {
          ...c,
          photoVersion: res.data.photoVersion,
          photoUrl: res.data.photoUrl,
        };
        return updated;
      })
    );
    return updated;
  }, []);

  const saveGroup = useCallback(async (payload: MailContactGroupSavePayload) => {
    const res = await saveMailContactGroup(payload);
    if (!res.ok) return null;
    const saved = res.data.group;
    setGroups((prev) => {
      const idx = prev.findIndex((g) => g.id === saved.id);
      if (idx === -1) {
        return [...prev, saved].sort((a, b) => a.name.localeCompare(b.name, "fr"));
      }
      const next = [...prev];
      next[idx] = saved;
      return next.sort((a, b) => a.name.localeCompare(b.name, "fr"));
    });
    return saved;
  }, []);

  const removeGroup = useCallback(async (id: number) => {
    const res = await deleteMailContactGroup(id);
    if (!res.ok) return false;
    setGroups((prev) => prev.filter((g) => g.id !== id));
    return true;
  }, []);

  const value = useMemo(
    () => ({
      contacts,
      groups,
      loading,
      refresh,
      collectEntries,
      searchSuggestions,
      searchRecipientSuggestions,
      getByEmail,
      getPhotoBlob,
      saveContact,
      promoteToBook,
      removeContact,
      uploadPhoto,
      saveGroup,
      removeGroup,
    }),
    [
      collectEntries,
      contacts,
      getByEmail,
      getPhotoBlob,
      groups,
      loading,
      promoteToBook,
      refresh,
      removeContact,
      removeGroup,
      saveContact,
      saveGroup,
      searchRecipientSuggestions,
      searchSuggestions,
      uploadPhoto,
    ]
  );

  return <MailContactsContext.Provider value={value}>{children}</MailContactsContext.Provider>;
}

export function useMailContacts(): MailContactsContextValue {
  const ctx = useContext(MailContactsContext);
  if (!ctx) {
    throw new Error("useMailContacts doit être utilisé dans MailContactsProvider");
  }
  return ctx;
}

export function useMailContactsOptional(): MailContactsContextValue | null {
  return useContext(MailContactsContext);
}
